import React from 'react';  
import { motion } from 'framer-motion';  
import { Terminal, Server, BookOpen, Package, Zap, ExternalLink, ArrowRight, Code } from 'lucide-react';  

const Developers = () => {  
  return (  
    <div className="min-h-screen pt-24 pb-16 bg-black text-white">  
      <div className="container mx-auto px-4">  
        <motion.h1  
          initial={{ opacity: 0, y: 20 }}  
          animate={{ opacity: 1, y: 0 }}  
          transition={{ duration: 0.6 }}  
          className="text-5xl md:text-6xl font-extrabold text-center mb-8 text-white"  
        >  
          Build on AVO Protocol
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}  
          transition={{ duration: 0.6, delay: 0.2 }}  
          className="text-xl text-gray-300 text-center mb-16 max-w-3xl mx-auto"  
        >
          Tools and resources for developers on AVO Protocol
        </motion.p>

        {/* Developer Tools Grid */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            Developer Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* CLI Tool */}
            <ToolCard
              icon={<Terminal size={48} />}
              title="AVO CLI"
              description="Command-line tool for wallet management, staking, governance and node operations."
              status="Operational"
              link="#cli-section"
              isInternal={true}
            />

            {/* Node */}
            <ToolCard
              icon={<Server size={48} />}
              title="AVO Node"
              description="Run your own validator node. Rust + RocksDB + libp2p. Complete setup and configuration documentation."
              status="Operational"
              link="#node-section"
              isInternal={true}
            />

            {/* Docs */}
            <ToolCard
              icon={<BookOpen size={48} />}
              title="Documentation"
              description="Complete GitBook with guides, tutorials, API reference and CLI/RPC usage examples."
              status="Operational"
              link="https://docs.avoprotocol.com"
              isInternal={true}
            />

            {/* SDK */}
            <ToolCard
              icon={<Package size={48} />}
              title="JavaScript SDK"
              description="SDK for TypeScript/JavaScript. Interact with AVO Protocol from your dApps with intuitive APIs."
              status="Coming Soon"
              link="#"
              isInternal={false}
            />

          </div>
        </section>

        {/* AVO STATION - DESTACADO */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="bg-black border-4 border-white p-8 md:p-12 relative overflow-hidden hover:border-blue-500 transition-all">
            
            <div className="relative z-10">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Zap className="w-12 h-12 text-blue-400" />
                <h2 className="text-4xl md:text-5xl font-bold text-white">AVO Station</h2>
                <span className="px-4 py-1 bg-green-500 text-black text-sm font-bold uppercase">Operational</span>
              </div>
              
              <p className="text-xl text-gray-300 text-center mb-8 max-w-3xl mx-auto">
                Your complete command center to interact with AVO Protocol. Staking, governance, treasury management, 
                performance analytics and more, all in an intuitive web interface.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-950 border-2 border-white p-6 hover:border-blue-400 transition-all">
                  <h3 className="text-xl font-bold mb-3 text-white">◉ Staking Dashboard</h3>
                  <p className="text-gray-400 text-sm">
                    Stake AVO, delegate to validators, and monitor your rewards in real-time with dynamically calculated APY.
                  </p>
                </div>
                <div className="bg-gray-950 border-2 border-white p-6 hover:border-purple-400 transition-all">
                  <h3 className="text-xl font-bold mb-3 text-white">◉ Governance Portal</h3>
                  <p className="text-gray-400 text-sm">
                    Vote on on-chain proposals, create new proposals, and visualize treasury status with quadratic voting.
                  </p>
                </div>
                <div className="bg-gray-950 border-2 border-white p-6 hover:border-cyan-400 transition-all">
                  <h3 className="text-xl font-bold mb-3 text-white">◉ Analytics Hub</h3>
                  <p className="text-gray-400 text-sm">
                    Live network metrics: current TPS, latency per shard, fee market, MEV captured, and validator health.
                  </p>
                </div>
              </div>

              <div className="flex justify-center gap-4 flex-wrap">
                <a
                  href="http://localhost:3001"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-white text-black font-bold hover:bg-blue-400 hover:text-white transition-all flex items-center gap-2 border-2 border-white"
                >
                  <Zap className="w-5 h-5" />
                  Access AVO Station
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </motion.section>

        {/* CLI Section */}
        <section id="cli-section" className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            AVO CLI - Command Line
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <CodeExample
              title="Create Wallet"
              code={`# Create new wallet
avo.exe wallet create

# Check balance
avo.exe wallet balance \\
  --address 0x...`}
              description="Ed25519 wallet management from CLI"
            />
            <CodeExample
              title="Admin Mint"
              code={`# Mint tokens (EIP-191 signature)
avo.exe admin mint \\
  --to 0xde525b... \\
  --amount 300000000`}
              description="Secure minting with cryptographic signature"
            />
            <CodeExample
              title="RPC Call"
              code={`curl -X POST localhost:9545 \\
  -H "Content-Type: application/json" \\
  -d '{"method": "avo_getPerformanceMetrics"}'`}
              description="Get metrics via RPC"
            />
          </div>
        </section>

        {/* Node Section */}
        <section id="node-section" className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            Run an AVO Node
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <InfoCard
              title="System Requirements"
              items={[
                "CPU: 4 cores minimum (8 recommended)",
                "RAM: 16GB minimum (32GB recommended)",
                "Storage: 500GB NVMe SSD",
                "Network: 100 Mbps symmetric",
                "OS: Ubuntu 22.04 LTS or similar"
              ]}
            />
            <InfoCard
              title="Technology Stack"
              items={[
                "Rust 1.70+ (main language)",
                "RocksDB (state persistence)",
                "libp2p (P2P networking)",
                "REVM (Rust EVM execution)",
                "BLS Signatures (cryptography)"
              ]}
            />
          </div>
        </section>

        {/* Network Endpoints */}
        <section className="mb-20">
          <h2 className="text-4xl font-bold text-center mb-12 text-white">
            Network Endpoints
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <EndpointCard
              title="HTTP RPC"
              endpoint="http://localhost:9545"
              description="Methods: eth_*, avo_adminMint, avo_getTreasuryAccounts, avo_getPerformanceMetrics"
            />
            <EndpointCard
              title="WebSocket RPC"
              endpoint="ws://localhost:9546"
              description="Real-time events: new blocks, pending transactions, network metrics"
            />
          </div>
        </section>

      </div>  
    </div>  
  );  
};  

// ToolCard Component
const ToolCard = ({ icon, title, description, status, link, isInternal }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-black border-2 border-white p-6 hover:border-gray-400 transition-all flex flex-col group"
  >
    <div className="flex justify-center mb-4">
      <div className="text-white group-hover:text-blue-400 transition-colors">{icon}</div>
    </div>
    <h3 className="text-2xl font-bold mb-3 text-white text-center">{title}</h3>
    <p className="text-gray-400 text-sm mb-4 flex-grow text-center">{description}</p>
    <div className="text-center mb-4">
      <span className={`px-3 py-1 text-xs font-bold ${
        status === 'Operational' ? 'bg-green-500 text-black' : 'bg-gray-700 text-gray-300'
      }`}>
        {status}
      </span>
    </div>
    {isInternal ? (
      <a
        href={link}
        className="bg-white text-black font-semibold py-2 px-4 transition-all hover:bg-blue-400 hover:text-white flex items-center justify-center border border-white"
      >
        Learn More <ArrowRight className="ml-2" size={16} />
      </a>
    ) : (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-transparent text-white font-semibold py-2 px-4 border border-white transition-all hover:bg-white hover:text-black flex items-center justify-center"
      >
        {status === 'Coming Soon' ? 'Coming Soon' : 'Access'} <ExternalLink className="ml-2" size={16} />
      </a>
    )}
  </motion.div>
);

// CodeExample Component
const CodeExample = ({ title, code, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-black border-2 border-white p-6 group hover:border-purple-500 transition-all"
  >
    <div className="flex items-center gap-2 mb-3">
      <Code className="w-5 h-5 text-white group-hover:text-purple-400 transition-colors" />
      <h3 className="text-lg font-bold text-white">{title}</h3>
    </div>
    <pre className="bg-gray-950 border border-gray-700 p-4 overflow-x-auto mb-3">
      <code className="text-green-400 text-sm font-mono">{code}</code>
    </pre>
    <p className="text-gray-400 text-xs">{description}</p>
  </motion.div>
);

// InfoCard Component
const InfoCard = ({ title, items }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-black border-2 border-white p-6 hover:border-cyan-500 transition-all"
  >
    <h3 className="text-2xl font-bold mb-4 text-white">{title}</h3>
    <ul className="space-y-2">
      {items.map((item, idx) => (
        <li key={idx} className="text-gray-300 flex items-start">
          <span className="text-cyan-400 mr-2">◉</span>
          <span className="text-sm">{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

// EndpointCard Component
const EndpointCard = ({ title, endpoint, description }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-black border-2 border-white p-6 hover:border-yellow-500 transition-all"
  >
    <h3 className="text-2xl font-bold mb-3 text-white">{title}</h3>
    <div className="bg-gray-950 border border-gray-700 p-3 mb-3">
      <code className="text-yellow-400 font-mono text-sm">{endpoint}</code>
    </div>
    <p className="text-gray-400 text-sm">{description}</p>
  </motion.div>
);

export default Developers;
